import { Tbody, Td, Tr } from '@chakra-ui/react';
import { flexRender, Table } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import React from 'react';

import styles from '@/components/virtual-table/styles.module.scss';
import { UI } from '@/constants/config';
import {
  DEFAULT_MAX_WIDTH_TABLE_SIZE,
  DEFAULT_TABLE_ROW_HEIGHT,
  DEFAULT_TABLE_ROW_WIDTH,
  NUMERICAL_TWO,
} from '@/constants/defaults';

interface VirtualTableBodyProps {
  noDataFoundMessage: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: Table<any>;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  uniqueRowKey: string;
  uniqueSelectedIds: Set<number | string>;
  dataTestId?: string;
  maxTableWidth?: string;
  tableRowHeight?: number;
  tableWidth?: string;
}

const VirtualTableBody = (props: VirtualTableBodyProps): React.JSX.Element => {
  const {
    dataTestId,
    maxTableWidth,
    noDataFoundMessage,
    table,
    tableContainerRef,
    tableRowHeight,
    tableWidth,
    uniqueRowKey,
    uniqueSelectedIds,
  } = props;

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    estimateSize: () => tableRowHeight ?? DEFAULT_TABLE_ROW_HEIGHT,
    getScrollElement: () => tableContainerRef.current,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  // When scroll container has no size (e.g. in jsdom tests), virtualizer returns no items.
  // Fall back to rendering all rows so content is still testable.
  const itemsToRender =
    // eslint-disable-next-line no-magic-numbers
    virtualItems.length > 0
      ? virtualItems
      : rows.map((row, index) => ({
          // eslint-disable-next-line no-magic-numbers
          end: (index + 1) * (tableRowHeight ?? DEFAULT_TABLE_ROW_HEIGHT),
          index,
          key: row.id,
          lane: 0,
          size: tableRowHeight ?? DEFAULT_TABLE_ROW_HEIGHT,
          start: index * (tableRowHeight ?? DEFAULT_TABLE_ROW_HEIGHT),
        }));

  if (!table.getAllColumns().length || !rows.length) {
    return (
      <Tbody
        data-testid={dataTestId}
        display={'flex'}
        maxWidth={maxTableWidth ?? `${DEFAULT_MAX_WIDTH_TABLE_SIZE}px`}
        position={'relative'}
        w={'100%'}
      >
        <Tr width="100%" display={'table'} key={noDataFoundMessage} textAlign={'center'}>
          <Td textAlign={'center'} width="100%">
            {noDataFoundMessage}
          </Td>
        </Tr>
      </Tbody>
    );
  }

  return (
    <Tbody
      data-testid={dataTestId}
      display={'flex'}
      flexDirection={'column'}
      height={`${rowVirtualizer.getTotalSize()}px`}
      maxWidth={maxTableWidth ?? `${DEFAULT_MAX_WIDTH_TABLE_SIZE}px`}
      position={'relative'}
      w={'100%'}
    >
      {itemsToRender.map((virtualRow) => {
        const row = rows[virtualRow.index];
        const isSelected = uniqueSelectedIds.has(row.original[uniqueRowKey]);

        return (
          <Tr
            backgroundColor={virtualRow.index % NUMERICAL_TWO ? '' : UI.oddRowColor}
            display={'table'}
            id={row.id}
            key={row.id}
            style={{
              display: 'flex',
              height: tableRowHeight ?? DEFAULT_TABLE_ROW_HEIGHT,
              maxWidth: maxTableWidth,
              position: 'absolute',
              transform: `translateY(${virtualRow.start}px)`,
              width: tableWidth ?? `${DEFAULT_TABLE_ROW_WIDTH}px`,
            }}
            width={tableWidth ?? DEFAULT_TABLE_ROW_WIDTH}
          >
            {row.getVisibleCells().map((cell) => {
              const cellClass = classNames(
                {
                  [styles.selected]: isSelected,
                },
                styles.text,
              );
              return (
                <Td
                  className={cellClass}
                  key={`table-cell-${cell.id}`}
                  maxWidth={maxTableWidth}
                  padding={0}
                  width={tableWidth ?? DEFAULT_TABLE_ROW_WIDTH}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              );
            })}
          </Tr>
        );
      })}
    </Tbody>
  );
};

export default VirtualTableBody;
